// Wires every extracted module together: scene/renderer setup, click
// dispatch, the contextual action bar, and the main animate loop.
// Step 10 of the migration plan (see CLAUDE.md).
//
// This file is deliberately thin — it's glue, not a place for new domain
// logic. Anything with real behavior lives in world/, entities/,
// systems/, economy/, or ui/hud.ts.

import * as THREE from 'three';

import {
  applySeed, surfaceYAt, worldToHex, terrainHeightAt, SEA_LEVEL, HEX_SIDE_DIRS, axialDistance, PROP_SCALE,
} from '@world/terrain';
import {
  waterInfoAt, habitatAt, updateWaterShimmer, WADE_SPEED_MULT, boatRipple, updateBoatRipple,
  setChoppiness, swellAt, swellTilt,
} from '@world/water';
import { activateFixedMap, deactivateFixedMap, FIXED_MAP_SEED } from '@world/fixedMap';
import {
  SKY_COLOR, ENV, ambient, sun, fill, rainPts, mistGroup, applyWeatherSeed, updateEnvironment, weatherForDay,
  configureFog,
} from '@world/weather';
import {
  lantern, lanternGroup, lanternGlow, updateLantern, toggleLantern, OIL_MAX,
} from '@systems/lantern';
import {
  blockGroup, seamGroup, decoGroup, villageGroup, wreckGroup, groundCoverGroup, loadStartArea, updateChunks,
  updateVillageMeshes, updateWrecks, nearbyWreck, LOAD_WORLD, clearDecoAt, updateWaterSwell,
} from '@world/chunks';

import { updateGrass } from '@world/grass';
import { treeGroup, stumpGroup, updateTreeShake, updateTreeRespawns, chopTree, getTreeMesh } from '@entities/trees';
import { fishGroup, trySpawnFish, updateFish } from '@entities/fish';
import {
  player, fisher, camera, mouseNDC, initCameraControls, initKeyboardControls, initJoystickControls,
  updateCamera, updatePlayerOnFoot, computeMoveVector, findSpawnPoint, startChopSwing, startRepairSwing, updateChop,
  setLocalAvatarVisual, updateSleepPose,
} from '@entities/player';
import {
  remotePlayerGroup, remoteVehicleGroup, remoteShelterGroup,
  upsertRemotePlayer, removeRemotePlayer, applyRemotePath, updateRemotePlayers, addRemoteShelter,
  setRemoteStats,
} from '@entities/remotePlayers';
import { setActiveAvatar, getActiveAvatar, avatarById } from '@entities/avatars';

import {
  cast, startCast, endCast, updateCast, CAST_RANGE, bobber, fishingLine,
  computeFishingBlockReason, type FishingConditions, type CastEvent,
} from '@systems/fishing';
import {
  jettyGroup, ghostGroup, splashGroup, vehicleGroup, canStand, standY, onJetty, addJettyDeck, canWade, isWading,
  placingJetty, beginJettyPlacement, cancelJettyPlacement, updateJettyGhost, confirmJettyPlacement,
  boat, inBoat, boardBoat, disembark, findDisembarkSpot, tryBuildBoat, boatSpotClear,
  BOAT_SPEED, BOAT_FLOAT_Y, updateSplashes, spawnSplash, oarTipLocal,
  cart, cartGrabbed, toggleGrabCart, loadFishToCart, unloadBoatToCart, updateCart,
  boatDamaged, jettyDamaged, applyStormDamage, repairBoat, repairJetty, nearJetty,
  JETTY_COST, BOAT_COST, JETTY_REPAIR_COST, BOAT_REPAIR_COST,
} from '@systems/building';
import { dive, startDive, updateDive, type DiveResult } from '@systems/diving';
import type { WreckSite } from '@systems/diving';
import {
  net, netGroup, castNet, updateNet, cancelNetIfActive, netCooldownRemaining, type NetEvent,
} from '@systems/fishingNet';

import {
  addWood, carryFish, boatFish, cartFish, CARRY_CAP, CART_CAP, BOAT_CAP,
  rodLevel, hasNet, saltLeft, makeFish, recordCatch, ageFishOvernight, checkSeasonEnd,
  setupRivals, rivals, simulateRivalDay, saltCatch, setRivalCount, coins, goodsValue,
  applyRivalSeed, localTrophyStats,
  type PlayerInfraFlags, SEASON_DAYS,
} from '@economy/market';
import {
  rest, sleepAnim, sleepQuality, wellRestedUntil, shelter, restSpeedMult, restBiteMod, nearShelter,
  doSleep, buildShelter, tickSleepAnim, applyRestDrain, SHELTER_COST,
} from '@economy/rest';
import { gameTimeNow, syncGameTimeFloor } from '@world/weather';

import {
  showToast, renderTally, updateStorageVisuals, updateMarketUI, updateSkyChip, initSkyChip,
  initOverlay, initEndScreen, showEndScreen, renderActionBar, type ActionItem,
  initLobby, renderLobby, revealGameUI, initTallyToggle, initFullscreenButton,
} from '@ui/hud';
import { nearestVillage } from '@economy/market';
import { connect, send, on, myId } from '@net/client';
import type { ClientMessage, PathWaypoint, PlayerInfo } from '@net/protocol';

const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

// ================= Scene setup =================
const scene = new THREE.Scene();
scene.background = new THREE.Color(SKY_COLOR);
// Derived from the terrain's own load distance rather than set by hand:
// fog has to be fully opaque *before* the loaded terrain runs out, or the
// world ends at a hard visible edge. The old fixed 16→34 started beyond the
// boundary entirely, so it never hid anything.
scene.fog = new THREE.Fog(SKY_COLOR, LOAD_WORLD * 0.34, LOAD_WORLD * 0.88);

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, IS_TOUCH ? 1.5 : 2));
document.body.appendChild(renderer.domElement);

scene.add(ambient, sun, fill, rainPts, mistGroup, lanternGroup);
configureFog(LOAD_WORLD);
scene.add(blockGroup, seamGroup, decoGroup, villageGroup, wreckGroup, groundCoverGroup);
scene.add(treeGroup, stumpGroup, fishGroup, fisher);
scene.add(bobber, fishingLine, boatRipple);
scene.add(jettyGroup, ghostGroup, splashGroup, vehicleGroup);
scene.add(netGroup);
scene.add(remotePlayerGroup, remoteVehicleGroup, remoteShelterGroup);

// ================= Game state =================
let started = false;
let seasonOver = false;
let worldLabel = '';

function fishingConditions(): FishingConditions {
  return {
    isStormy: ENV.weather === 'Storm',
    inBoat,
    isNight: ENV.night,
    isFullMoon: ENV.fullMoon,
    isRaining: ENV.weather === 'Rain',
    rodLevel,
    restBiteMod: restBiteMod() + getActiveAvatar().stats.biteMod,
  };
}

function restText(): string {
  let s = '💤 Rest ' + Math.round(rest);
  if (gameTimeNow() < wellRestedUntil) s += ' ✨ well rested';
  else if (rest < 30) s += ' — exhausted!';
  else if (rest < 60) s += ' — tired';
  return s;
}

function infraFlags(): PlayerInfraFlags {
  return { hasBoat: !!boat, hasCart: !!cart, hasShelter: !!shelter };
}

function refreshTally(): void {
  renderTally(restText(), infraFlags());
}

function initWorld(seed: number, now: number, useFixedMap: boolean): void {
  applySeed(useFixedMap ? FIXED_MAP_SEED : seed);
  applyWeatherSeed();
  applyRivalSeed();
  if (useFixedMap) activateFixedMap(); else deactivateFixedMap();
  findSpawnPoint();
  fisher.position.set(player.x, surfaceYAt(player.x, player.z), player.z);
  loadStartArea(now);
}

// ================= Overlay / start & end screens =================
initOverlay((choice) => {
  worldLabel = choice.worldLabel;
  setActiveAvatar(choice.avatarId);
  setLocalAvatarVisual(getActiveAvatar().visual);
  initWorld(choice.seed, performance.now(), choice.useFixedMap);
  setupRivals();
  started = true;
  refreshTally();
});
initEndScreen(() => location.reload());
initSkyChip();
initTallyToggle();
initFullscreenButton();

// ================= Multiplayer lobby =================
function connectAndSend(msg: ClientMessage): void {
  connect().then(() => send(msg)).catch((err: Error) => showToast(err.message));
}

initLobby({
  onCreateRoom: (name) => connectAndSend({ type: 'create_room', name }),
  onJoinRoom: (code, name) => connectAndSend({ type: 'join_room', code, name }),
  onSetReady: (ready) => send({ type: 'set_ready', ready }),
  onSetAvatar: (avatarId) => send({ type: 'set_avatar', avatarId }),
  onSetFixedMap: (useFixedMap) => send({ type: 'update_settings', settings: { useFixedMap } }),
  onStartGame: () => send({ type: 'start_game' }),
  onLeaveRoom: () => send({ type: 'leave_room' }),
});

let roomPlayers: PlayerInfo[] = [];

function syncRoomPlayers(players: PlayerInfo[]): void {
  const selfId = myId();
  const newIds = new Set(players.map((p) => p.id));
  roomPlayers.forEach((p) => { if (p.id !== selfId && !newIds.has(p.id)) removeRemotePlayer(p.id); });
  roomPlayers = players;
}

on('room_joined', (msg) => {
  syncRoomPlayers(msg.room.players);
  renderLobby(msg.room, msg.playerId);
});
on('room_update', (msg) => {
  syncRoomPlayers(msg.room.players);
  const id = myId();
  if (id) renderLobby(msg.room, id);
});
on('error', (msg) => showToast(msg.message));
on('player_path_update', (msg) => {
  syncGameTimeFloor(msg.gameTime);
  if (msg.id === myId()) return;
  const info = roomPlayers.find((p) => p.id === msg.id);
  upsertRemotePlayer(msg.id, info?.name ?? 'Fisherman', avatarById(info?.avatarId).visual);
  applyRemotePath(msg.id, msg.waypoints);
});
on('jetty_deck_placed', (msg) => {
  if (msg.id === myId()) return;
  msg.cells.forEach((c) => { addJettyDeck(c.q, c.r, msg.angle); clearDecoAt(c.q, c.r); });
});
on('shelter_placed', (msg) => {
  if (msg.id === myId()) return;
  addRemoteShelter(msg.x, msg.z, msg.heading);
});
on('score_update', (msg) => {
  if (msg.id === myId()) return;
  setRemoteStats(msg.id, msg.base, msg.stats);
});
on('game_start', (msg) => {
  worldLabel = msg.settings.worldLabel;
  syncRoomPlayers(msg.players);
  const me = msg.players.find((p) => p.id === myId());
  if (me) {
    setActiveAvatar(me.avatarId);
    setLocalAvatarVisual(getActiveAvatar().visual);
  }
  setRivalCount(msg.settings.rivalCount);
  initWorld(msg.settings.seed, performance.now(), msg.settings.useFixedMap);
  setupRivals(msg.players.map((p) => avatarById(p.avatarId).name));
  started = true;
  refreshTally();
  revealGameUI(msg.settings.worldLabel, msg.settings.seed);
  send({ type: 'score_update', base: coins + goodsValue(), stats: localTrophyStats(infraFlags()) });
});

// ================= Camera / input =================
initCameraControls(renderer.domElement, {
  isStarted: () => started,
  onTap: handleClick,
});
initKeyboardControls();
document.addEventListener('keydown', (e) => {
  if (e.code === 'Escape' && placingJetty) cancelJettyPlacement();
});
initJoystickControls(document.getElementById('joystick')!, document.getElementById('joyKnob')!);

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

// ================= Click interaction =================
const raycaster = new THREE.Raycaster();
raycaster.far = 60;

function handleClick(): void {
  if (seasonOver || sleepAnim > 0) return;
  if (placingJetty) {
    updateJettyGhost(camera, mouseNDC); // aim from the tap/click position (touch has no hover)
    const r = confirmJettyPlacement();
    showToast(r.success ? '🔨 Jetty built! (-' + JETTY_COST + ' Wood)' : 'Point at open water beside you.');
    if (r.success) {
      refreshTally();
      // Anything growing where the decking now runs gets cleared, or the
      // mangroves come straight up through the boards.
      r.cells?.forEach((c) => clearDecoAt(c.q, c.r));
      if (r.cells) send({ type: 'jetty_deck_placed', cells: r.cells, angle: r.angle! });
    }
    return;
  }
  raycaster.setFromCamera(mouseNDC, camera);

  const treeHit = raycaster.intersectObjects(treeGroup.children, true)[0];
  const blockHit = raycaster.intersectObjects(blockGroup.children, false)[0];
  const fishHit = raycaster.intersectObjects(fishGroup.children, false)[0];

  const candidates: Array<{ d: number; kind: 'tree' | 'fish' | 'block'; hit: THREE.Intersection }> = [];
  if (treeHit) candidates.push({ d: treeHit.distance, kind: 'tree', hit: treeHit });
  if (fishHit) candidates.push({ d: fishHit.distance - 0.3, kind: 'fish', hit: fishHit }); // slight priority
  if (blockHit) candidates.push({ d: blockHit.distance, kind: 'block', hit: blockHit });
  if (!candidates.length) return;
  candidates.sort((a, b) => a.d - b.d);
  const c = candidates[0];

  if (c.kind === 'tree') {
    const tk = c.hit.object.userData.treeKey as string | undefined;
    const g = tk ? getTreeMesh(tk) : undefined;
    if (!g || !tk) return;
    const playerHex = worldToHex(player.x, player.z);
    const treeHex = worldToHex(g.position.x, g.position.z);
    if (axialDistance(playerHex.q, playerHex.r, treeHex.q, treeHex.r) > 1) {
      showToast('Walk closer to chop that tree.');
      return;
    }
    // Turn to face the tree and swing the axe
    player.heading = Math.atan2(g.position.z - player.z, g.position.x - player.x);
    startChopSwing();
    const result = chopTree(tk, gameTimeNow());
    if (result.felled) {
      addWood(result.wood);
      refreshTally();
      showToast('🪓 Timber! +' + result.wood + ' Wood');
    } else {
      showToast('🪓 Chop! (' + result.hits + '/' + result.requiredHits + ')');
    }
    return;
  }

  // Fishing: clicked water or a fish
  let tx: number, tz: number;
  if (c.kind === 'fish') {
    tx = c.hit.object.position.x;
    tz = c.hit.object.position.z;
  } else {
    if (!c.hit.object.userData.isWater) return; // clicked land: nothing
    tx = c.hit.point.x;
    tz = c.hit.point.z;
  }
  const w = waterInfoAt(tx, tz);
  if (!w) return;
  const blockReason = computeFishingBlockReason(fishingConditions());
  if (blockReason) { showToast(blockReason); return; }
  const dist = Math.hypot(tx - player.x, tz - player.z);
  if (dist > CAST_RANGE) { showToast('Too far to cast — walk closer to the shore.'); return; }
  if (inBoat) {
    if (boatFish.length >= BOAT_CAP) { showToast('The boat hold is full — unload your catch first.'); return; }
  } else {
    const cartNear = !!(cart && Math.hypot(cart.x - player.x, cart.z - player.z) < 2.5);
    const cartSpace = cartNear && cartFish.length < CART_CAP;
    if (carryFish.length >= CARRY_CAP && !cartSpace) {
      showToast('Your basket is full — store or sell your fish first.');
      return;
    }
  }
  startCast(tx, tz, w.surfaceY);
}

// ================= Cast / dive / net event handling =================
function handleCastEvent(event: CastEvent): void {
  if (event.type === 'blocked') { showToast(event.reason + ' Line reeled in.'); return; }
  if (event.type === 'no-bite') { showToast('…No bite this time.'); return; }
  const { typeName, x, z } = event;
  let stored: string | null = null;
  if (inBoat) {
    if (boatFish.length < BOAT_CAP) { boatFish.push(makeFish(typeName)); stored = 'the boat hold'; }
  } else {
    const cartNear = !!(cart && Math.hypot(cart.x - player.x, cart.z - player.z) < 2.5);
    if (cartNear && cartFish.length < CART_CAP) { cartFish.push(makeFish(typeName)); stored = 'the cart'; }
    else if (carryFish.length < CARRY_CAP) { carryFish.push(makeFish(typeName)); stored = 'your basket'; }
  }
  if (stored) {
    recordCatch(typeName, x, z);
    refreshTally();
    updateStorageVisuals();
    showToast('🎣 Caught a ' + typeName + '! → ' + stored);
  } else {
    showToast('A ' + typeName + '! ...but no room left to keep it.');
  }
}

function handleDiveResult(result: DiveResult): void {
  const cleaned = (c: boolean) => (c ? ' The wreck is picked clean.' : '');
  switch (result.kind) {
    case 'small-cache': showToast('💰 A small cache — +' + result.amount + ' coins!' + cleaned(result.cleaned)); break;
    case 'pearl': showToast('🦪 A pearl from the wreck!' + cleaned(result.cleaned)); break;
    case 'koban': showToast('🪙 An old koban! +' + result.amount + ' coins!' + cleaned(result.cleaned)); break;
    case 'wreck-empty': showToast('The wreck is picked clean.'); break;
    case 'clam-pearl': showToast('🦪 A pearl inside the shell!'); break;
    case 'clam-empty': showToast('…just an empty clam.'); break;
  }
  refreshTally();
}

function handleNetEvent(event: NetEvent): void {
  if (event.type === 'blocked') { showToast(event.reason + ' The net was hauled in early.'); return; }
  if (event.type === 'hauled') {
    refreshTally();
    updateStorageVisuals();
    showToast('🕸️ Hauled in ' + event.count + ' fish!');
  } else {
    showToast('🕸️ The net came up empty.');
  }
}

// ================= New-day orchestration =================
function onNewDay(prevDay: number): void {
  const spoiled = ageFishOvernight();
  if (spoiled) {
    updateStorageVisuals();
    showToast('🐟 ' + spoiled + ' fish spoiled overnight and had to be tossed.');
  }
  const prevWeather = weatherForDay(prevDay);
  rivals.forEach((rv, i) => simulateRivalDay(rv, prevWeather, performance.now(), prevDay, i));
  refreshTally();
  if (checkSeasonEnd(prevDay + 1)) {
    seasonOver = true;
    showEndScreen(worldLabel, infraFlags());
    return;
  }
  if (prevWeather !== 'Storm') return;
  const hit = applyStormDamage();
  const parts: string[] = [];
  if (hit.boat) parts.push('the boat');
  if (hit.jetty) parts.push('the jetty');
  if (parts.length) showToast('⛈️ The storm battered ' + parts.join(' and ') + '! Repairs needed.');
}

// ================= Contextual action bar =================
function actDisembark(): void {
  const r = disembark();
  if (r.success) {
    let msg = '⚓ Boat tethered.';
    if (r.fishCarriedAshore) {
      updateStorageVisuals();
      msg += ' 🧺 Took ' + r.fishCarriedAshore + ' fish in the basket.';
    }
    refreshTally();
    showToast(msg);
  } else {
    showToast('Row next to land or the jetty to step out.');
  }
}

function actUnloadBoatToCart(): void {
  const n = unloadBoatToCart();
  refreshTally();
  updateStorageVisuals();
  showToast(n ? '→ ' + n + ' fish moved from the boat to the cart.' : 'The cart is full.');
}

function actStartDive(kind: 'wreck' | 'shell', wk: WreckSite | null): void {
  const r = startDive(kind, wk);
  showToast(r.success ? '🤿 He slips over the side...' : (r.reason || ''));
}

function actCastNet(): void {
  const r = castNet(fishingConditions());
  if (r.success) { showToast('🕸️ Net cast — give it a moment...'); return; }
  showToast(r.reason === 'boat-full' ? 'The boat hold is full — unload your catch first.' : (r.reason || ''));
}

function actSaltCatch(): void {
  const r = saltCatch();
  refreshTally();
  if (r.salted) { showToast('🧂 Salted ' + r.salted + ' fish — they\'ll keep forever, at 60% value.'); return; }
  showToast(r.reason === 'no-salt' ? 'No salt left — buy more at a market.' : 'Nothing to salt.');
}

function actDoSleep(quality: 'collapse' | 'shelter' | 'rough'): void {
  const msg = doSleep(quality, cancelNetIfActive);
  refreshTally();
  showToast(msg);
}

function actBeginJettyPlacement(): void {
  const r = beginJettyPlacement();
  if (r.success) { showToast('Point at the water to aim the jetty, then click to build. (Esc to cancel)'); return; }
  showToast(r.reason === 'not-enough-wood' ? 'Need ' + JETTY_COST + ' Wood to build a jetty.' : 'Stand on the shore to build.');
}

function actTryBuildBoat(): void {
  const r = tryBuildBoat();
  if (r.success) { showToast('🛶 Boat built and tethered to the jetty!'); return; }
  const msgs: Record<string, string> = {
    'jetty-damaged': 'Repair the jetty before launching a boat.',
    'not-enough-wood': 'Need ' + BOAT_COST + ' Wood to build a boat.',
    'no-open-water': 'No open water beside the jetty for a boat.',
  };
  showToast(msgs[r.reason || ''] || 'Cannot build a boat here.');
}

function actRepairBoat(): void {
  const r = repairBoat();
  if (r.success) startRepairSwing();
  showToast(r.success ? '🔨 Boat repaired and seaworthy!' : 'Need ' + BOAT_REPAIR_COST + ' Wood to repair the boat.');
}

function actBoardBoat(): void {
  const r = boardBoat();
  if (r.success) { showToast('⛵ Aboard! Row with WASD, cast from the water.'); return; }
  if (r.reason === 'boat-damaged') showToast('The boat needs repairs first (' + BOAT_REPAIR_COST + ' wood).');
}

function actRepairJetty(): void {
  const r = repairJetty();
  if (r.success) startRepairSwing();
  showToast(r.success ? '🔨 Jetty repaired!' : 'Need ' + JETTY_REPAIR_COST + ' Wood to repair the jetty.');
}

function actToggleLantern(): void {
  const lit = toggleLantern();
  if (lit === null) { showToast('🪔 No oil left — buy a flask at a market.'); return; }
  showToast(lit ? '🪔 The lantern is lit.' : '🪔 Lantern doused.');
}

function actBuildShelter(): void {
  const r = buildShelter();
  if (r.success && shelter) {
    scene.add(shelter.mesh);
    showToast('🏠 Shelter built — sleep here for the best rest.');
    send({ type: 'shelter_placed', x: shelter.x, z: shelter.z, heading: player.heading });
    return;
  }
  const shelterFailures: Record<string, string> = {
    'not-enough-wood': 'Need ' + SHELTER_COST + ' Wood to build a shelter.',
    'too-close-to-market': 'Too close to the village — build further out.',
  };
  showToast(shelterFailures[r.reason ?? ''] ?? 'No room for a shelter here — face open ground.');
}

function actToggleGrabCart(): void {
  toggleGrabCart();
  showToast(cartGrabbed ? '🛒 Pulling the cart.' : '🛒 Cart set down.');
}

function actLoadFishToCart(): void {
  const n = loadFishToCart();
  refreshTally();
  updateStorageVisuals();
  showToast(n ? '→ ' + n + ' fish loaded into the cart.' : 'The cart is full.');
}

function updateActions(): void {
  const list: ActionItem[] = [];
  if (started) {
    if (placingJetty) {
      list.push({ id: 'cancelJetty', label: '✖ Cancel Jetty', fn: cancelJettyPlacement });
    } else if (inBoat) {
      if (!dive.active) {
        if (findDisembarkSpot()) list.push({ id: 'dis', label: '⚓ Disembark & Tether', fn: actDisembark });
        if (boat && cart && boatFish.length && cartFish.length < CART_CAP
            && Math.hypot(cart.x - boat.x, cart.z - boat.z) < 2.6) {
          list.push({ id: 'unload', label: '🐟 Unload Boat → Cart', fn: actUnloadBoatToCart });
        }
        if (boat) {
          const wk = nearbyWreck(boat.x, boat.z, 2.3);
          if (wk && wk.loot > 0) {
            list.push({ id: 'divew', label: '🤿 Dive the Wreck', fn: () => actStartDive('wreck', wk) });
          } else {
            const bh = worldToHex(boat.x, boat.z);
            if (habitatAt(bh.q, bh.r) === 'shellfish') {
              list.push({ id: 'dives', label: '🤿 Dive for Shellfish', fn: () => actStartDive('shell', null) });
            }
          }
        }
        if (hasNet && !net.active && netCooldownRemaining() <= 0) {
          list.push({ id: 'net', label: '🕸️ Cast Net', fn: actCastNet });
        }
        if (saltLeft > 0 && boatFish.some((f) => !f.salted)) {
          list.push({ id: 'salt', label: '🧂 Salt Catch (' + saltLeft + ')', fn: actSaltCatch });
        }
        if (ENV.frac >= 0.7 || ENV.night || rest < 35) {
          list.push({ id: 'sleepR', label: '🛏️ Sleep (rough)', fn: () => actDoSleep('rough') });
        }
      }
    } else {
      const p = worldToHex(player.x, player.z);
      const onLand = terrainHeightAt(p.q, p.r) >= SEA_LEVEL;
      if (!dive.active && isWading(player.x, player.z) && habitatAt(p.q, p.r) === 'shellfish') {
        list.push({ id: 'dives', label: '🤿 Dive for Shellfish', fn: () => actStartDive('shell', null) });
      }
      if (onLand) {
        const shoreside = HEX_SIDE_DIRS.some(([dq, dr]) => terrainHeightAt(p.q + dq, p.r + dr) < SEA_LEVEL);
        if (shoreside) list.push({ id: 'jetty', label: '🔨 Build Jetty (' + JETTY_COST + ' Wood)', fn: actBeginJettyPlacement });
      }
      if (onJetty(player.x, player.z) && !boat) {
        list.push({ id: 'boat', label: '🛶 Build Boat (' + BOAT_COST + ' Wood)', fn: actTryBuildBoat });
      }
      if (boat && Math.hypot(boat.x - player.x, boat.z - player.z) < 1.7) {
        if (boatDamaged) list.push({ id: 'fixboat', label: '🔨 Repair Boat (' + BOAT_REPAIR_COST + ' Wood)', fn: actRepairBoat });
        else if (Math.hypot(boat.x - player.x, boat.z - player.z) < 1.5) {
          list.push({ id: 'board', label: '⛵ Board Boat', fn: actBoardBoat });
        }
      }
      if (jettyDamaged && nearJetty(1.9)) {
        list.push({ id: 'fixjetty', label: '🔨 Repair Jetty (' + JETTY_REPAIR_COST + ' Wood)', fn: actRepairJetty });
      }
      if (!shelter && onLand) {
        list.push({ id: 'shelter', label: '🏠 Build Shelter (' + SHELTER_COST + ' Wood)', fn: actBuildShelter });
      }
      if (lantern.owned) {
        // The action bar only rebuilds when an id changes, so the oil level
        // has to be part of the id or the label would freeze at whatever it
        // read when the button was first drawn. Bucketed to 10% so that's
        // four or five rebuilds across a full tank, not one per frame.
        const oilPct = Math.round(Math.max(0, Math.min(1, lantern.oil / OIL_MAX)) * 10) * 10;
        list.push(lantern.lit
          ? { id: 'lantOff' + oilPct, label: '🪔 Douse Lantern (' + oilPct + '% oil)', fn: actToggleLantern }
          : { id: 'lantOn' + oilPct, label: '🪔 Light Lantern (' + oilPct + '% oil)', fn: actToggleLantern });
      }
      if (saltLeft > 0 && (carryFish.some((f) => !f.salted)
          || (!!cart && Math.hypot(cart.x - player.x, cart.z - player.z) < 2.5 && cartFish.some((f) => !f.salted)))) {
        list.push({ id: 'salt', label: '🧂 Salt Catch (' + saltLeft + ')', fn: actSaltCatch });
      }
      if (ENV.frac >= 0.7 || ENV.night || rest < 35) {
        if (nearShelter()) list.push({ id: 'sleepS', label: '🛏️ Sleep in Shelter ✨', fn: () => actDoSleep('shelter') });
        else if (!isWading(player.x, player.z)) list.push({ id: 'sleepR', label: '🛏️ Sleep (rough)', fn: () => actDoSleep('rough') });
      }
      if (cart) {
        const nearCart = Math.hypot(cart.x - player.x, cart.z - player.z) < 1.6;
        if (cartGrabbed) list.push({ id: 'drop', label: '🛒 Set Down Cart', fn: actToggleGrabCart });
        else if (nearCart) list.push({ id: 'grab', label: '🛒 Grab Cart', fn: actToggleGrabCart });
        if ((nearCart || cartGrabbed) && carryFish.length && cartFish.length < CART_CAP) {
          list.push({ id: 'load', label: '🐟 Load Fish → Cart', fn: actLoadFishToCart });
        }
      }
      if (boat && cart && boatFish.length && cartFish.length < CART_CAP
          && Math.hypot(boat.x - player.x, boat.z - player.z) < 1.7
          && Math.hypot(cart.x - boat.x, cart.z - boat.z) < 2.6) {
        list.push({ id: 'unload', label: '🐟 Unload Boat → Cart', fn: actUnloadBoatToCart });
      }
    }
  }
  renderActionBar(list);
}

// ================= Boat-rowing movement =================
// Not player movement (see entities/player.ts's header) — moving while
// aboard drives the boat instead of the walk cycle.
function angleDiff(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function updateBoatRowing(dt: number, now: number): void {
  if (!boat) return;
  const b = boat; // capture: TS can't narrow a mutable import across closures
  const oars = b.mesh.userData.oars as THREE.Mesh[];
  const move = computeMoveVector(sleepAnim > 0);
  let dx = move.dx, dz = move.dz;
  if (move.moving && !dive.active) {
    dx /= move.len; dz /= move.len;
    const step = BOAT_SPEED * restSpeedMult() * getActiveAvatar().stats.speedMult * dt;
    const tries: Array<[number, number]> = [
      [b.x + dx * step, b.z + dz * step],
      [b.x + dx * step, b.z],
      [b.x, b.z + dz * step],
    ];
    for (const [nx, nz] of tries) {
      if (boatSpotClear(nx, nz, b.heading)) {
        b.x = nx; b.z = nz;
        break;
      }
    }
    const th = Math.atan2(dz, dx);
    b.heading += angleDiff(b.heading, th) * Math.min(1, dt * 6);
    if (cast.active) { endCast(); showToast('Line reeled in.'); }
    // Rowing stroke: oars sweep back and forth and dip into the water
    b.rowPhase += dt * 7;
    const sNow = Math.sin(b.rowPhase);
    oars.forEach((oar) => {
      oar.rotation.y = sNow * 0.55;
      oar.rotation.x = (oar.userData.baseRotX as number) + Math.cos(b.rowPhase) * 0.28 * (oar.userData.side as number);
    });
    // Splash at the catch of each stroke, at both oar tips
    if (sNow > 0 && b.lastSin <= 0) {
      oars.forEach((oar) => {
        oarTipLocal.set(-0.15, 0, (oar.userData.side as number) * 0.55);
        const wp = b.mesh.localToWorld(oarTipLocal.clone());
        spawnSplash(wp.x, wp.z);
      });
    }
    b.lastSin = sNow;
  } else {
    // Ease oars back to resting position
    oars.forEach((oar) => {
      oar.rotation.y += (0 - oar.rotation.y) * Math.min(1, dt * 5);
      oar.rotation.x += ((oar.userData.baseRotX as number) - oar.rotation.x) * Math.min(1, dt * 5);
    });
  }
  player.x = b.x;
  player.z = b.z;
  player.walking = false;
  const bobY = BOAT_FLOAT_Y + Math.sin(now * 0.0015) * 0.012 * PROP_SCALE + swellAt(b.x, b.z, now);
  b.mesh.position.set(b.x, bobY, b.z);
  applyBoatAttitude(b.mesh, b.x, b.z, b.heading, now);
  if (!cast.active) player.heading = b.heading;
  fisher.position.set(b.x, bobY + 0.07 * PROP_SCALE, b.z); // the stretch is lengthwise only, so this is unchanged
  fisher.rotation.y = -player.heading;
  fisher.rotation.z = Math.sin(now * 0.0015) * 0.02;
}

// Heading, plus pitch and roll taken from the slope of the swell under the
// hull. The damage list rides on the roll — a boat taking water lists to one
// side — and has to be applied here rather than set once, since these are
// rewritten every frame.
function applyBoatAttitude(mesh: THREE.Object3D, x: number, z: number, heading: number, now: number): void {
  const tilt = swellTilt(x, z, heading, now);
  mesh.rotation.y = -heading;
  mesh.rotation.z = tilt.pitch;
  mesh.rotation.x = tilt.roll + (boatDamaged ? 0.18 : 0);
}

// ================= Main loop =================
let last = performance.now();
let fishTimer = 0;
let marketTimer = 0;

// Position/vehicle sync: sample locally at a steady cadence, then send the
// accumulated waypoints as one batch every PATH_SEND_MS (2-5s, per design —
// see @net/protocol's PathWaypoint) instead of one message per tick. The
// receiver (entities/remotePlayers.ts) replays the batch across that same
// window so remote motion still looks continuous despite the coarser rate.
// Score sync piggybacks on the same cadence (see the pathSendTimer block
// below) rather than running its own timer — it doesn't need to be any
// fresher than position does.
const PATH_SAMPLE_MS = 200;
const PATH_SEND_MS = 3000;
let pathSampleTimer = 0;
let pathSendTimer = 0;
let pathBuffer: PathWaypoint[] = [];
let pathBatchStartMs = performance.now();

function animate(): void {
  requestAnimationFrame(animate);
  const now = performance.now();
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;

  const envResult = updateEnvironment(dt, {
    running: started && !seasonOver,
    sleeping: sleepAnim > 0,
    playerX: player.x,
    playerZ: player.z,
    scene,
    lanternLight: lanternGlow(),
  });
  // Held out in front of him, at about hand height. Positioned from the
  // fisherman rather than the player coords so it follows him onto the boat
  // without a separate case.
  const lanternOut = 0.24 * PROP_SCALE;
  if (updateLantern(dt,
      fisher.position.x + Math.cos(player.heading) * lanternOut,
      fisher.position.y + 0.30 * PROP_SCALE,
      fisher.position.z + Math.sin(player.heading) * lanternOut, now)) {
    showToast('🪔 The lantern gutters out — it needs oil.');
  }
  if (envResult.restDrain) applyRestDrain(envResult.restDrain * getActiveAvatar().stats.restDrainMult);
  if (envResult.dayChanged !== null) onNewDay(envResult.dayChanged);

  if (started && !seasonOver) {
    tickSleepAnim(dt);
    updateSleepPose(dt, sleepAnim > 0, sleepQuality === 'rough');
    if (inBoat && boat) {
      updateBoatRowing(dt, now);
    } else {
      const wading = isWading(player.x, player.z);
      const r = updatePlayerOnFoot(dt, {
        canStand,
        canWade,
        standY,
        speedMult: restSpeedMult() * getActiveAvatar().stats.speedMult * (wading ? WADE_SPEED_MULT : 1),
        sleeping: sleepAnim > 0,
        castActive: cast.active,
      });
      if (r.interruptedCast) { endCast(); showToast('Line reeled in.'); }
    }
    updateCamera();
    updateChunks(dt, now);
    updateFish(dt, now, player.x, player.z);
    const castEvent = updateCast(dt, now, fishingConditions());
    if (castEvent) handleCastEvent(castEvent);
    updateTreeShake(dt);
    updateChop(dt);
    updateSplashes(dt);
    updateJettyGhost(camera, mouseNDC);
    updateActions();
    updateCart(dt);
    const diveResult = updateDive(dt);
    if (diveResult) handleDiveResult(diveResult);
    const netEvent = updateNet(dt, now, fishingConditions());
    if (netEvent) handleNetEvent(netEvent);

    marketTimer += dt;
    if (marketTimer > 0.3) {
      marketTimer = 0;
      nearestVillage(player.x, player.z, now);
      updateVillageMeshes();
      updateMarketUI(now);
      updateSkyChip(ENV, SEASON_DAYS, weatherForDay);
      updateWrecks();
      updateTreeRespawns(gameTimeNow());
      refreshTally();
      if (rest <= 0 && sleepAnim <= 0) actDoSleep('collapse');
    }
    if (boat && !inBoat) {
      boat.mesh.position.y = BOAT_FLOAT_Y + Math.sin(now * 0.0015) * 0.012 * PROP_SCALE + swellAt(boat.x, boat.z, now);
      applyBoatAttitude(boat.mesh, boat.x, boat.z, boat.heading, now);
    }
    updateBoatRipple(boat ? boat.x : 0, boat ? boat.z : 0, boat ? boat.heading : 0, now, !!boat);
    fishTimer += dt;
    if (fishTimer > 0.7) { fishTimer = 0; trySpawnFish(player.x, player.z); }

    pathSampleTimer += dt * 1000;
    if (pathSampleTimer > PATH_SAMPLE_MS) {
      pathSampleTimer = 0;
      pathBuffer.push({
        t: now - pathBatchStartMs,
        x: player.x, z: player.z, heading: player.heading,
        boat: boat ? { x: boat.x, z: boat.z, heading: boat.heading } : null,
        cart: cart ? { x: cart.x, z: cart.z, heading: cart.heading } : null,
      });
    }
    pathSendTimer += dt * 1000;
    if (pathSendTimer > PATH_SEND_MS && pathBuffer.length > 0) {
      pathSendTimer = 0;
      send({ type: 'player_path_update', waypoints: pathBuffer });
      pathBuffer = [];
      pathBatchStartMs = now;
      send({ type: 'score_update', base: coins + goodsValue(), stats: localTrophyStats(infraFlags()) });
    }
    updateRemotePlayers(dt);
  } else {
    updateCamera();
  }

  // The sea gets up in bad weather. Set before anything reads swellAt(),
  // so the tiles, the boat and its wake all agree on the same surface.
  setChoppiness(ENV.weather === 'Storm' ? 1 : ENV.weather === 'Rain' ? 0.5 : ENV.weather === 'Cloudy' ? 0.18 : 0);
  updateWaterSwell(now);
  updateWaterShimmer(now);
  updateGrass(now, dt);
  renderer.render(scene, camera);
}
animate();
